import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Chip } from "../Chip";
import { Popper, PopperContent, PopperTrigger } from "../Popper";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var MAX_CHIPS_ROWS = 1;
var EXTRA_WIDTH_FROM_CLOSE = 28;
export function ChipGroup(_ref) {
  var items = _ref.items,
    selected = _ref.selected,
    onDelete = _ref.onDelete,
    onClearAll = _ref.onClearAll,
    _ref$maxChipsRows = _ref.maxChipsRows,
    maxChipsRows = _ref$maxChipsRows === void 0 ? MAX_CHIPS_ROWS : _ref$maxChipsRows,
    _ref$extraWidthFromCl = _ref.extraWidthFromClose,
    extraWidthFromClose = _ref$extraWidthFromCl === void 0 ? EXTRA_WIDTH_FROM_CLOSE : _ref$extraWidthFromCl,
    clearButtonLabel = _ref.clearButtonLabel,
    _ref$chipVariant = _ref.chipVariant,
    chipVariant = _ref$chipVariant === void 0 ? 'close' : _ref$chipVariant;
  var chipsContainerRef = useRef(null);
  var overflowChipRef = useRef(null);
  var virtualChipRef = useRef(null);
  var _useCalculateChipOver = useCalculateChipOverflow({
      chipsContainerRef: chipsContainerRef,
      overflowChipRef: overflowChipRef,
      virtualChipRef: virtualChipRef,
      selectedOptions: selected,
      items: items,
      maxChipsRows: maxChipsRows,
      extraWidthFromClose: extraWidthFromClose
    }),
    visibleChips = _useCalculateChipOver.visibleChips;
  return /*#__PURE__*/_jsxs("div", {
    className: "tw-space-y-1.2",
    children: [/*#__PURE__*/_jsx(Chip, {
      ref: virtualChipRef,
      className: "tw-absolute -tw-left-full -tw-z-7 tw-opacity-0"
    }), /*#__PURE__*/_jsxs("div", {
      ref: chipsContainerRef,
      className: clsx('tw-pt-0.8 tw-flex tw-items-center tw-gap-0.8 tw-flex-wrap', selected.length === 0 && 'tw-hidden'),
      children: [selected.map(function (value, index) {
        var _items$find;
        return index >= visibleChips ? null : /*#__PURE__*/_jsx(Chip, {
          variant: chipVariant,
          onClose: function onClose() {
            return onDelete === null || onDelete === void 0 ? void 0 : onDelete(value);
          },
          children: (_items$find = items.find(function (el) {
            return el.value === value;
          })) === null || _items$find === void 0 ? void 0 : _items$find.label
        }, value);
      }), /*#__PURE__*/_jsxs(Popper, {
        children: [/*#__PURE__*/_jsx(PopperTrigger, {
          children: /*#__PURE__*/_jsxs("div", {
            ref: overflowChipRef,
            className: "tw-border tw-border-solid tw-rounded-2 tw-py-0.8 tw-px-1.2 tw-bg-primary-light-400 tw-text-neutral-light-100 tw-font-body5",
            hidden: visibleChips >= selected.length,
            children: ["+", selected.length - visibleChips]
          })
        }), /*#__PURE__*/_jsx(PopperContent, {
          removePadding: true,
          removePortal: true,
          align: "end",
          children: /*#__PURE__*/_jsx("ul", {
            className: "tw-flex tw-flex-col tw-max-h-28 tw-overflow-auto",
            children: selected.map(function (value, index) {
              var _items$find2;
              return index < visibleChips ? null : /*#__PURE__*/_jsx("li", {
                className: "tw-px-2.4 tw-py-1.2",
                children: /*#__PURE__*/_jsx(Chip, {
                  variant: chipVariant,
                  onClose: function onClose() {
                    return onDelete === null || onDelete === void 0 ? void 0 : onDelete(value);
                  },
                  children: (_items$find2 = items.find(function (el) {
                    return el.value === value;
                  })) === null || _items$find2 === void 0 ? void 0 : _items$find2.label
                })
              }, value);
            })
          })
        })]
      })]
    }), selected.length > 0 && clearButtonLabel && /*#__PURE__*/_jsx("button", {
      type: "button",
      className: "tw-text-primary-light-500 tw-font-link1",
      onClick: onClearAll,
      children: clearButtonLabel
    })]
  });
}
function useCalculateChipOverflow(_ref2) {
  var chipsContainerRef = _ref2.chipsContainerRef,
    overflowChipRef = _ref2.overflowChipRef,
    virtualChipRef = _ref2.virtualChipRef,
    selectedOptions = _ref2.selectedOptions,
    items = _ref2.items,
    maxChipsRows = _ref2.maxChipsRows,
    extraWidthFromClose = _ref2.extraWidthFromClose;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    visibleChipsState = _useState2[0],
    setVisibleChips = _useState2[1];
  var effectKey = selectedOptions.length;

  /**
   * Use an useEffect to make sure this logic runs after the html natives nodes are rendered correctly
   * so we can safely calculate it's height, width, rect, etc
   * In case this doesn't work as expected we must use the useLayoutEffect to calculate the node rects after the browser repaints the screen
   */
  useEffect(function () {
    var _overflowChipRef$curr, _overflowChipRef$curr2;
    var container = chipsContainerRef.current;
    var virtualChip = virtualChipRef.current;
    if (!container || !container.firstChild || !virtualChip) {
      setVisibleChips(0);
      return;
    }
    var flexGap = 8;
    var overflowChipWidth = (_overflowChipRef$curr = (_overflowChipRef$curr2 = overflowChipRef.current) === null || _overflowChipRef$curr2 === void 0 ? void 0 : _overflowChipRef$curr2.getBoundingClientRect().width) !== null && _overflowChipRef$curr !== void 0 ? _overflowChipRef$curr : 0;
    var lines = 1;
    var visibleChips = 0;
    var totalWidth = 0;
    selectedOptions.forEach(function (option) {
      var _items$find$label, _items$find3;
      var chip = virtualChip;
      chip.innerHTML = (_items$find$label = (_items$find3 = items.find(function (el) {
        return el.value === option;
      })) === null || _items$find3 === void 0 ? void 0 : _items$find3.label) !== null && _items$find$label !== void 0 ? _items$find$label : '';
      var chipWidth = chip.getBoundingClientRect().width + extraWidthFromClose;
      if (totalWidth + chipWidth + (lines >= maxChipsRows ? overflowChipWidth + flexGap : 0) > container.offsetWidth) {
        lines += 1;
        totalWidth = 0;
      }
      if (lines <= maxChipsRows) {
        totalWidth += chipWidth + flexGap;
        visibleChips += 1;
      }
    });
    setVisibleChips(visibleChips);
  }, [effectKey]);
  return {
    visibleChips: visibleChipsState
  };
}